/*
    Be aware that SponsoredOfferBox component relies heavily on the layout of this component.
    In case of making any change in layout, please also update SponsoredOfferBox

    TODO: Maybe it's worth to make some abstraction/shared component if there are more cases like this
 */
import React, {memo, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {OfferBoxBase} from "../../../atoms/OfferBoxBase";
import {useResponsiveOpenLink} from "../../../hooks/use_responsive_open_link";
import {showPropertiesClick} from "../../../tracking/algolytics_hits/show_properties_click";
import {getAmplitudeItemListContextWithStaticFallback} from "../../../tracking/amplitude/get_amplitude_item_list_context_with_static_fallback";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackOfferSelectItemWithListContext} from "../../../tracking/amplitude/track_offer_select_item_with_list_context";
import {ILatestQuery} from "../../../utils/latest_query";
import {useOfferLink} from "../../detail/hooks/use_offer_link";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {useOfferBoxAnalytics} from "../../hooks/use_offer_box_analytics";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {hitGTMOfferSelectItem} from "../../tracking/ecommerce_events/offer/hit_gtm_offer_select_item";
import {IOfferBoxGTMSource} from "../../types/IOfferBoxGTMSource";
import {IOfferBoxOffer} from "../../types/IOfferBoxOffer";
import {createTrackedOffer} from "../../utils/create_tracked_offer";
import {NoTagPlug} from "./NoTagPlug";
import {OfferBoxInfoSectionExtended} from "./OfferBoxInfoSectionExtended";
import {OfferBoxTopSectionSlider} from "./OfferBoxTopSectionSlider";

export interface IOfferBox {
    isArchived?: boolean;
    isUnavailable?: boolean;
    offer: IOfferBoxOffer;
    onShowOfferDetailsBtnClick?: (offer: IOfferBoxOffer) => void;
    offerDetailsBtnText?: string;
    unlinkVendor?: boolean;
    className?: string;
    shouldLinkToOffer?: boolean;
    onClick?: () => void;
    index: number;
    fetchPriority?: "high" | "low" | "auto";
    titleTag?: React.ElementType;
    trackingMeta: {
        listId: string;
        listName: string;
        source: IOfferBoxGTMSource;
        tileEvents: {
            imageClick: (tileIndex: number) => void;
            buttonClick: (tileIndex: number) => void;
            linkClick: (tileIndex: number) => void;
            logoClick: (tileIndex: number) => void;
        };
    };
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    dataTestId?: string;
    latestQuery?: ILatestQuery & Record<string, string | string[] | null>;
}

export const OfferBox = memo((props: IOfferBox) => {
    const {
        offer,
        isArchived,
        isUnavailable,
        fetchPriority,
        trackingMeta,
        index,
        className,
        dataTestId,
        latestQuery,
        offerDetailsBtnText,
        onClick,
        onShowOfferDetailsBtnClick,
        shouldLinkToOffer,
        titleTag,
        unlinkVendor,
        amplitudeItemListContext
    } = props;

    const [isInfoPanelOpened, setIsInfoPanelOpened] = useState(false);

    const viewType = useSelector((state: IRPStore) => state.viewType.current);

    const offerLink = useOfferLink(offer);
    const openResponsiveOfferLink = useResponsiveOpenLink(offerLink);
    const {onPrevClick, onNextClick, reportOfferSelect} = useOfferBoxAnalytics({
        offerId: offer.id,
        offerBoxIndex: index || 0
    });
    const amplitudeSelectItemListContext = getAmplitudeItemListContextWithStaticFallback({
        dynamicItemListContext: amplitudeItemListContext,
        trackingMeta
    });

    const onInfoPanelToggleClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsInfoPanelOpened(!isInfoPanelOpened);
        event.preventDefault();
    };
    const onInfoPanelCloseClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsInfoPanelOpened(false);
    };

    const trackOfferSelectItem = () => {
        trackOfferSelectItemWithListContext({
            offer,
            index,
            itemListContext: amplitudeSelectItemListContext
        });
    };

    const createOfferDetailUrlWithPendingContext = () => {
        return createOfferListingAmplitudePendingDetailUrl({
            url: offerLink,
            itemId: offer.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const onSlideClick = () => {
        trackingMeta.tileEvents.imageClick(index);

        reportOfferSelect();
        hitGTMOfferSelectItem({
            offer,
            listId: trackingMeta.listId,
            listName: trackingMeta.listName
        });
        trackOfferSelectItem();
        return createOfferDetailUrlWithPendingContext();
    };

    const onOfferButtonClick = () => {
        trackingMeta.tileEvents.buttonClick(index);
        reportOfferSelect();
        hitGTMOfferSelectItem({
            offer,
            listId: trackingMeta.listId,
            listName: trackingMeta.listName
        });
        trackOfferSelectItem();

        if (shouldLinkToOffer) {
            return createOfferDetailUrlWithPendingContext();
        }
    };

    const onOfferLinkClick = () => {
        trackingMeta.tileEvents.linkClick(index);

        reportOfferSelect();
        hitGTMOfferSelectItem({
            offer,
            listId: trackingMeta.listId,
            listName: trackingMeta.listName
        });
        trackOfferSelectItem();
        return createOfferDetailUrlWithPendingContext();
    };

    const onLogoClick = () => {
        trackingMeta.tileEvents.logoClick(index);
    };

    return (
        <OfferBoxBase data-id={offer.id} data-testid={dataTestId} className={className} onClick={onClick}>
            {(isUnavailable || isArchived) && <div className={boxOverlay} onClick={openResponsiveOfferLink} />}
            <OfferBoxTopSectionSlider
                offer={offer}
                offerLink={offerLink}
                fetchPriority={fetchPriority}
                index={index}
                onInfoPanelToggleClick={onInfoPanelToggleClick}
                onPrevClick={onPrevClick}
                onNextClick={onNextClick}
                onSlideClick={onSlideClick}
                isArchived={isArchived}
                isUnavailable={isUnavailable}
            />

            <OfferBoxInfoSectionExtended
                offer={offer}
                offerLink={offerLink}
                isArchived={isArchived}
                isUnavailable={isUnavailable}
                unlinkVendor={unlinkVendor}
                isInfoPanelOpened={isInfoPanelOpened}
                titleTag={titleTag}
                onInfoPanelCloseClick={onInfoPanelCloseClick}
                onOfferLinkClick={onOfferLinkClick}
                latestQuery={latestQuery}
                onLogoClick={onLogoClick}
            />

            {onShowOfferDetailsBtnClick ? (
                <div className={buttonWrapperStyle}>
                    <Button
                        dataTestId={OFFER_TEST_IDS.LISTING_OFFER_BOX.PROPERTY_VIEW_BUTTON}
                        variant="outlined_secondary"
                        disabled={isArchived || isUnavailable}
                        onClick={(event) => {
                            const offerDetailUrl = onOfferButtonClick();

                            if (shouldLinkToOffer) {
                                event.preventDefault();
                                openResponsiveOfferLink(offerDetailUrl);
                                return;
                            }
                            viewType != null && showPropertiesClick(viewType, createTrackedOffer(offer), offer.vendor);
                            onShowOfferDetailsBtnClick && onShowOfferDetailsBtnClick(offer);
                        }}
                        href={shouldLinkToOffer ? offerLink : undefined}
                        target={shouldLinkToOffer ? "_blank" : undefined}
                        fullWidth
                    >
                        {offerDetailsBtnText ?? `Zobacz ${getOfferTypeNamePlural(offer.type)}`}
                    </Button>
                </div>
            ) : (
                <NoTagPlug height="6.4rem" />
            )}
        </OfferBoxBase>
    );
});

const buttonWrapperStyle = css({
    pt: 0,
    pb: 2,
    px: 2
});

const boxOverlay = css({
    position: "absolute",
    inset: 0,
    backgroundColor: "var(--colors-gray-100)",
    opacity: "50%",
    zIndex: 5,
    cursor: "pointer"
});
/*
    Be aware that SponsoredOfferBox component relies heavily on the layout of this component.
    In case of making any change in layout, please also update SponsoredOfferBox

    TODO: Maybe it's worth to make some abstraction/shared component if there are more cases like this
 */
import React, {memo, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {OfferBoxBase} from "../../../atoms/OfferBoxBase";
import {useResponsiveOpenLink} from "../../../hooks/use_responsive_open_link";
import {showPropertiesClick} from "../../../tracking/algolytics_hits/show_properties_click";
import {getAmplitudeItemListContextWithStaticFallback} from "../../../tracking/amplitude/get_amplitude_item_list_context_with_static_fallback";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackOfferSelectItemWithListContext} from "../../../tracking/amplitude/track_offer_select_item_with_list_context";
import {ILatestQuery} from "../../../utils/latest_query";
import {useOfferLink} from "../../detail/hooks/use_offer_link";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {useOfferBoxAnalytics} from "../../hooks/use_offer_box_analytics";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {hitGTMOfferSelectItem} from "../../tracking/ecommerce_events/offer/hit_gtm_offer_select_item";
import {IOfferBoxGTMSource} from "../../types/IOfferBoxGTMSource";
import {IOfferBoxOffer} from "../../types/IOfferBoxOffer";
import {createTrackedOffer} from "../../utils/create_tracked_offer";
import {NoTagPlug} from "./NoTagPlug";
import {OfferBoxInfoSectionExtended} from "./OfferBoxInfoSectionExtended";
import {OfferBoxTopSectionSlider} from "./OfferBoxTopSectionSlider";

export interface IOfferBox {
    isArchived?: boolean;
    isUnavailable?: boolean;
    offer: IOfferBoxOffer;
    onShowOfferDetailsBtnClick?: (offer: IOfferBoxOffer) => void;
    offerDetailsBtnText?: string;
    unlinkVendor?: boolean;
    className?: string;
    shouldLinkToOffer?: boolean;
    onClick?: () => void;
    index: number;
    fetchPriority?: "high" | "low" | "auto";
    titleTag?: React.ElementType;
    trackingMeta: {
        listId: string;
        listName: string;
        source: IOfferBoxGTMSource;
        tileEvents: {
            imageClick: (tileIndex: number) => void;
            buttonClick: (tileIndex: number) => void;
            linkClick: (tileIndex: number) => void;
            logoClick: (tileIndex: number) => void;
        };
    };
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    dataTestId?: string;
    latestQuery?: ILatestQuery & Record<string, string | string[] | null>;
}

export const OfferBox = memo((props: IOfferBox) => {
    const {
        offer,
        isArchived,
        isUnavailable,
        fetchPriority,
        trackingMeta,
        index,
        className,
        dataTestId,
        latestQuery,
        offerDetailsBtnText,
        onClick,
        onShowOfferDetailsBtnClick,
        shouldLinkToOffer,
        titleTag,
        unlinkVendor,
        amplitudeItemListContext
    } = props;

    const [isInfoPanelOpened, setIsInfoPanelOpened] = useState(false);

    const viewType = useSelector((state: IRPStore) => state.viewType.current);

    const offerLink = useOfferLink(offer);
    const openResponsiveOfferLink = useResponsiveOpenLink(offerLink);
    const {onPrevClick, onNextClick, reportOfferSelect} = useOfferBoxAnalytics({
        offerId: offer.id,
        offerBoxIndex: index || 0
    });
    const amplitudeSelectItemListContext = getAmplitudeItemListContextWithStaticFallback({
        dynamicItemListContext: amplitudeItemListContext,
        trackingMeta
    });

    const onInfoPanelToggleClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsInfoPanelOpened(!isInfoPanelOpened);
        event.preventDefault();
    };
    const onInfoPanelCloseClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsInfoPanelOpened(false);
    };

    const trackOfferSelectItem = () => {
        trackOfferSelectItemWithListContext({
            offer,
            index,
            itemListContext: amplitudeSelectItemListContext
        });
    };

    const createOfferDetailUrlWithPendingContext = () => {
        return createOfferListingAmplitudePendingDetailUrl({
            url: offerLink,
            itemId: offer.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const onSlideClick = () => {
        trackingMeta.tileEvents.imageClick(index);

        reportOfferSelect();
        hitGTMOfferSelectItem({
            offer,
            listId: trackingMeta.listId,
            listName: trackingMeta.listName
        });
        trackOfferSelectItem();
        return createOfferDetailUrlWithPendingContext();
    };

    const onOfferButtonClick = () => {
        trackingMeta.tileEvents.buttonClick(index);
        reportOfferSelect();
        hitGTMOfferSelectItem({
            offer,
            listId: trackingMeta.listId,
            listName: trackingMeta.listName
        });
        trackOfferSelectItem();

        if (shouldLinkToOffer) {
            return createOfferDetailUrlWithPendingContext();
        }
    };

    const onOfferLinkClick = () => {
        trackingMeta.tileEvents.linkClick(index);

        reportOfferSelect();
        hitGTMOfferSelectItem({
            offer,
            listId: trackingMeta.listId,
            listName: trackingMeta.listName
        });
        trackOfferSelectItem();
        return createOfferDetailUrlWithPendingContext();
    };

    const onLogoClick = () => {
        trackingMeta.tileEvents.logoClick(index);
    };

    return (
        <OfferBoxBase data-id={offer.id} data-testid={dataTestId} className={className} onClick={onClick}>
            {(isUnavailable || isArchived) && <div className={boxOverlay} onClick={openResponsiveOfferLink} />}
            <OfferBoxTopSectionSlider
                offer={offer}
                offerLink={offerLink}
                fetchPriority={fetchPriority}
                index={index}
                onInfoPanelToggleClick={onInfoPanelToggleClick}
                onPrevClick={onPrevClick}
                onNextClick={onNextClick}
                onSlideClick={onSlideClick}
                isArchived={isArchived}
                isUnavailable={isUnavailable}
            />

            <OfferBoxInfoSectionExtended
                offer={offer}
                offerLink={offerLink}
                isArchived={isArchived}
                isUnavailable={isUnavailable}
                unlinkVendor={unlinkVendor}
                isInfoPanelOpened={isInfoPanelOpened}
                titleTag={titleTag}
                onInfoPanelCloseClick={onInfoPanelCloseClick}
                onOfferLinkClick={onOfferLinkClick}
                latestQuery={latestQuery}
                onLogoClick={onLogoClick}
            />

            {onShowOfferDetailsBtnClick ? (
                <div className={buttonWrapperStyle}>
                    <Button
                        dataTestId={OFFER_TEST_IDS.LISTING_OFFER_BOX.PROPERTY_VIEW_BUTTON}
                        variant="outlined_secondary"
                        disabled={isArchived || isUnavailable}
                        onClick={(event) => {
                            const offerDetailUrl = onOfferButtonClick();

                            if (shouldLinkToOffer) {
                                event.preventDefault();
                                openResponsiveOfferLink(offerDetailUrl);
                                return;
                            }
                            viewType != null && showPropertiesClick(viewType, createTrackedOffer(offer), offer.vendor);
                            onShowOfferDetailsBtnClick && onShowOfferDetailsBtnClick(offer);
                        }}
                        href={shouldLinkToOffer ? offerLink : undefined}
                        target={shouldLinkToOffer ? "_blank" : undefined}
                        fullWidth
                    >
                        {offerDetailsBtnText ?? `Zobacz ${getOfferTypeNamePlural(offer.type)}`}
                    </Button>
                </div>
            ) : (
                <NoTagPlug height="6.4rem" />
            )}
        </OfferBoxBase>
    );
});

const buttonWrapperStyle = css({
    pt: 0,
    pb: 2,
    px: 2
});

const boxOverlay = css({
    position: "absolute",
    inset: 0,
    backgroundColor: "var(--colors-gray-100)",
    opacity: "50%",
    zIndex: 5,
    cursor: "pointer"
});
